import {useEffect, useRef, type ReactNode} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {useRive} from '@rive-app/react-canvas';

import {Session} from 'nutshell-core/session';

import {Stack} from 'shells/layout/stack/stack';

import type {UserEmailCalendarStatusesFragment} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';
import {useScrollRestoration} from '../../../util/hooks/use-scroll-restoration';

import {PageWrapper} from '../../layout/page-wrapper';

import {ExplorePageHeader} from './explore-page-header';
import {useMarkExploreAttentionBadgeAsSeen} from './graphql/hooks/use-mark-explore-attention-badge-as-seen';
import type {ExplorePageVariant} from './types';

import styles from './explore-page.module.css';

const SCROLL_STORAGE_KEY = 'explore-page-scroll';

interface Props {
    children: ReactNode;
    variant: ExplorePageVariant;
    userEmailCalendarStatuses?: UserEmailCalendarStatusesFragment | null;
    onEmailCalendarAuthSuccess?: (provider: 'google' | 'microsoft') => void;
}

export function ExplorePageBase(props: Props) {
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    useScrollRestoration(scrollContainerRef, SCROLL_STORAGE_KEY);

    const hasMarkedAsSeen = useRef<boolean>(false);
    const dispatch = useDispatch();
    const shouldShowBadge = useSelector(Session.getShouldShowExploreAttentionBadge);
    const {markExploreAttentionBadgeAsSeen} = useMarkExploreAttentionBadgeAsSeen();

    useEffect(() => {
        if (shouldShowBadge && !hasMarkedAsSeen.current) {
            hasMarkedAsSeen.current = true;
            dispatch({
                type: Session.ActionTypes.SESSION_UPDATE_EXPLORE_ATTENTION_BADGE,
                payload: {shouldShowExploreAttentionBadge: false},
            });
            markExploreAttentionBadgeAsSeen();
        }
    }, [shouldShowBadge, dispatch, markExploreAttentionBadgeAsSeen]);

    const {RiveComponent} = useRive({
        src: '/include/rive/trial-gathering/explore-footer.riv',
        autoplay: true,
    });

    useEffect(() => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_EXPLORE_PAGE_VIEWED, {
            variant: props.variant,
        });
    }, [props.variant]);

    return (
        <PageWrapper minWidth={800}>
            <div
                ref={scrollContainerRef}
                className='flex flex-dir-col full-height justify-sb overflow-y-auto border-top-left-8'
            >
                <ExplorePageHeader
                    variant={props.variant}
                    userEmailCalendarStatuses={props.userEmailCalendarStatuses}
                    onEmailCalendarAuthSuccess={
                        props.onEmailCalendarAuthSuccess ?? (() => undefined)
                    }
                />
                <div className={styles['content-container']}>
                    <Stack spacing={48} isFullWidth={true}>
                        {props.children}
                    </Stack>
                </div>
                <div className={styles['footer-container']}>
                    <RiveComponent />
                </div>
            </div>
        </PageWrapper>
    );
}
